/**
 * API クライアント管理
 */

import { Configuration, BaseAPI } from "@acomo/client";
import * as apis from "@acomo/client";
import { resolveConfig, ENV_KEYS, type AcomoConfig } from "./config-store.js";

/** fetch タイムアウト（ミリ秒）: ネットワーク障害時の無限待機を防ぐ */
const FETCH_TIMEOUT_MS = 30_000;

/** AbortController でタイムアウトを付与した fetch ラッパー */
function fetchWithTimeout(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  return fetch(input, { ...init, signal: controller.signal }).finally(() =>
    clearTimeout(timer),
  );
}

/** 必須項目のラベルと環境変数名 */
const REQUIRED_KEYS: Array<{ key: keyof AcomoConfig; envKey: string }> = [
  { key: 'baseUrl', envKey: ENV_KEYS.baseUrl },
  { key: 'tenantId', envKey: ENV_KEYS.tenantId },
  { key: 'accessToken', envKey: ENV_KEYS.accessToken },
];

export interface OperationEntry {
  instance: BaseAPI;
  method: string;
}

/**
 * 保存済み設定から Configuration インスタンスを生成
 * baseUrl / tenantId / accessToken が未設定の場合はエラーメッセージを出して throw する
 */
export function createConfiguration(): Configuration {
  const config = resolveConfig();
  if (!config) {
    throw new Error("認証されていません。`acomo login` を実行するか、環境変数 (ACOMO_ACCESS_TOKEN, ACOMO_TENANT_ID, ACOMO_BASE_URL) を設定してください。");
  }

  const missing = REQUIRED_KEYS.filter(({ key }) => !config[key] || String(config[key]).trim() === '');
  if (missing.length > 0) {
    const keys = missing.map(({ key }) => key).join(', ');
    const envVars = missing.map(({ envKey }) => envKey).join(', ');
    const message = missing.length === 1
      ? `${missing[0].key} が未設定です。\`acomo login\` を実行するか、環境変数 ${missing[0].envKey} を設定してください。`
      : `次の設定が不足しています: ${keys}。\`acomo login\` を実行するか、環境変数 ${envVars} を設定してください。`;
    throw new Error(message);
  }

  const params = {
    basePath: config.baseUrl,
    accessToken: config.accessToken,
    apiKey: config.tenantId,
    fetchApi: fetchWithTimeout,
  };
  return new Configuration(params);
}

/**
 * @acomo/client からエクスポートされた API クラスを抽出
 * (BaseAPI を継承し、名前が "Api" で終わるもの)
 */
function extractApiClasses(): Record<string, typeof BaseAPI> {
  const apiClasses: Record<string, typeof BaseAPI> = {};

  for (const [name, value] of Object.entries(apis)) {
    if (
      typeof value === "function" &&
      name.endsWith("Api") &&
      value.prototype instanceof BaseAPI
    ) {
      apiClasses[name] = value as typeof BaseAPI;
    }
  }

  return apiClasses;
}

/** buildOperationRegistry のキャッシュ（起動中に複数回呼ばれても再構築しない） */
let cachedRegistry: Map<string, OperationEntry> | null = null;

/**
 * 全 API クラスのメソッドを走査し operationId → { instance, method } のマッピングを構築
 * *Raw, constructor, with* 系の内部メソッドは除外
 */
export function buildOperationRegistry(
  config: Configuration,
): Map<string, OperationEntry> {
  if (cachedRegistry) return cachedRegistry;
  const registry = new Map<string, OperationEntry>();
  const apiClasses = extractApiClasses();

  for (const [className, ApiClass] of Object.entries(apiClasses)) {
    // インスタンス化
    const instance = new (ApiClass as any)(config) as BaseAPI;

    // プロトタイプからメソッド名を取得
    const methods = Object.getOwnPropertyNames(ApiClass.prototype).filter(
      (m) => m !== "constructor" && !m.endsWith("Raw") && !m.startsWith("with"), // withMiddleware 等を除外
    );

    for (const method of methods) {
      registry.set(method, { instance, method });
    }
  }

  cachedRegistry = registry;
  return registry;
}
